import { useQuery } from '@tanstack/react-query'
import { ReactNode } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import { CustomCultureStatusResponse } from '~/api/backend/customCultureStatusAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import CultureStatusIcon from '~/components/icons/CultureStatusIcon'
import Select from '~/components/Select'
import cs from './culture_status_picker.scss'

interface MenuOption {
  key: string
  label: ReactNode
}

export default function CultureStatusPicker({
  currentStatus,
  onChange,
}: {
  currentStatus: CustomCultureStatusResponse
  onChange: (status: CustomCultureStatusResponse) => void
}) {
  const client = useBackendClient()
  const { data } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_CULTURE_STATUS_LIST],
    queryFn: () => client.customCultureStatuses.getAll(),
  })

  const statuses = data ?? []
  const activeStatuses = statuses
    .filter(s => s.category === 'active')
    .sort((a, b) => {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })

  const terminatedStatuses = statuses
    .filter(s => s.category === 'terminated')
    .sort((a, b) => {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })

  const menuItems = [...activeStatuses, ...terminatedStatuses].map(status => ({
    key: status.name,
    label: (
      <div className={cs.option}>
        <CultureStatusIcon kind={status.icon} /> {status.name}
      </div>
    ),
  }))

  const activeStatus = currentStatus.is_archived
    ? {
        // Key is required for Select to work. We use an arbitrary key here.
        key: 'statusArchived',
        label: 'Select a status',
      }
    : {
        key: currentStatus.name,
        label: (
          <div className={cs.option}>
            <CultureStatusIcon kind={currentStatus.icon} /> {currentStatus.name}
          </div>
        ),
      }

  return statuses.length > 0 ? (
    <div>
      <Select<MenuOption>
        items={menuItems}
        itemKey='key'
        itemLabelKey='label'
        activeItem={activeStatus}
        onChange={item => {
          onChange(statuses.find(s => s.name === item.key)!)
        }}
        triggerClassName={cs.selectContainer}
      />
    </div>
  ) : null
}
